import { ProviderFileViewModel } from '../models/Entities/AzureFiles/AzureFileUpload';
import {
  IMPORT_MAPPING_FIELDS,
  IMPORTABLE_FILE_EXTENSIONS,
  IMPORTABLE_PROVIDER_FILE_TYPES,
  ImportAssignmentStatus,
  ImportedProviderConsumption,
  ImportMappingColumns,
  ImportMappingField,
  ImportMappingOverride,
  ImportNormalization,
  ImportProviderFileConsumptionsRequest,
  LOW_CONFIDENCE_THRESHOLD,
} from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { Protocol } from '../models/Entities/Protocols/Protocol';
import { ProtocolPatient } from '../models/Entities/Protocols/ProtocolPatient';
import { ErrorType } from '../models/HttpError';
import { toTenantRelativePath } from './StorageManagement.Functions';

/** Estado de asignación de un consumo importado (lo calcula el front con los campos del consumo). */
export const assignmentStatusOf = (consumption: ImportedProviderConsumption): ImportAssignmentStatus => {
  if (consumption.protocolConsumedServiceId) return 'Generated';
  if (consumption.protocolId && consumption.patientNumberInProtocol) return 'Assigned';
  return 'Unassigned';
};

/** Se puede (re)asignar mientras no se generó el consumo de protocolo (la API responde 409 si ya se generó). */
export const canAssign = (consumption: ImportedProviderConsumption): boolean => assignmentStatusOf(consumption) !== 'Generated';

/** Se puede generar el consumo de protocolo de un consumo asignado y todavía no generado. */
export const canGenerate = (consumption: ImportedProviderConsumption): boolean => assignmentStatusOf(consumption) === 'Assigned';

/** El consumo quedó sin concepto de contrato (o con otra alerta del backend). */
export const hasWarning = (consumption: ImportedProviderConsumption): boolean => !!consumption.markedAsHaveWarnings;

const idsWhere = (consumptions: ImportedProviderConsumption[], selected: string[], predicate: (item: ImportedProviderConsumption) => boolean) => {
  const wanted = new Set(selected);
  return consumptions.filter((item) => wanted.has(item.id) && predicate(item)).map((item) => item.id);
};

/** Ids seleccionados que se pueden asignar. */
export const assignableIds = (consumptions: ImportedProviderConsumption[], selected: string[]): string[] => idsWhere(consumptions, selected, canAssign);

/** Ids seleccionados que se pueden generar. */
export const generatableIds = (consumptions: ImportedProviderConsumption[], selected: string[]): string[] => idsWhere(consumptions, selected, canGenerate);

/** Alterna un id en la selección. */
export const toggleId = (selected: string[], id: string): string[] =>
  selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id];

/** Selecciona / deselecciona todos los ids visibles, conservando el resto de la selección. */
export const toggleAll = (selected: string[], visibleIds: string[]): string[] => {
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  if (allSelected) return selected.filter((id) => !visibleIds.includes(id));
  return Array.from(new Set([...selected, ...visibleIds]));
};

/** Archivos que se pueden importar (MP 029): tipo factura (1) o servicios realizados (2) con extensión xlsx / xls / csv / pdf / docx. */
export const isImportableFile = (file: ProviderFileViewModel): boolean =>
  IMPORTABLE_PROVIDER_FILE_TYPES.includes(Number(file.fileType)) &&
  IMPORTABLE_FILE_EXTENSIONS.includes(String(file.fileExtension ?? '').replace(/^\./, '').toLowerCase());

/** Nombre completo del archivo (la API devuelve nombre y extensión por separado). */
export const fileDisplayName = (file: Pick<ProviderFileViewModel, 'fileName' | 'fileExtension'>): string => {
  const extension = String(file.fileExtension ?? '').replace(/^\./, '');
  const name = file.fileName ?? '';
  if (!extension || name.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) return name;
  return `${name}.${extension}`;
};

/** Archivos importables, del más reciente al más antiguo. */
export const importableFiles = (files: ProviderFileViewModel[]): ProviderFileViewModel[] =>
  files
    .filter(isImportableFile)
    .slice()
    .sort((a, b) => new Date(b.uploadedAt ?? 0).getTime() - new Date(a.uploadedAt ?? 0).getTime());

/** Ruta relativa al tenant del archivo (para la vista previa). */
export const filePreviewPath = (file: Pick<ProviderFileViewModel, 'blobPath'>): string => toTenantRelativePath(file.blobPath);

/** "id - nombre" del protocolo. */
export const protocolLabel = (protocol: Pick<Protocol, 'id' | 'title'> | undefined): string =>
  protocol ? `${protocol.id} - ${protocol.title ?? ''}`.trim() : '';

/** "nro. en protocolo - historia clínica" del paciente. */
export const patientLabel = (patient: Pick<ProtocolPatient, 'numberInProtocol' | 'medicalRecordNumber'>): string =>
  patient.medicalRecordNumber ? `${patient.numberInProtocol} - ${patient.medicalRecordNumber}` : patient.numberInProtocol;

/** Filtra protocolos por id o nombre. */
export const filterProtocols = (protocols: Protocol[], query: string): Protocol[] => {
  const text = query.trim().toLowerCase();
  if (!text) return protocols;
  return protocols.filter((protocol) => protocolLabel(protocol).toLowerCase().includes(text));
};

/** Filtra pacientes por número en protocolo o historia clínica. */
export const filterPatients = (patients: ProtocolPatient[], query: string): ProtocolPatient[] => {
  const text = query.trim().toLowerCase();
  if (!text) return patients;
  return patients.filter((patient) => patientLabel(patient).toLowerCase().includes(text));
};

/** Monto con separadores locales y la moneda; "—" si no hay valor. */
export const formatImportAmount = (amount: number | null | undefined, currency: string | null | undefined, locale?: string): string => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';
  const value = Number(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency.toUpperCase()} ${value}` : value;
};

/** Fecha corta (dd/mm/aaaa) de una fecha ISO; "—" si no hay. */
export const formatImportDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

/** Texto del error de la API: los `Errors[]` si vienen, si no el mensaje general (como getUploadErrorMessage). */
export const getApiErrorMessage = (error: unknown): string => {
  const typed = error as Partial<ErrorType> | undefined;
  const details = (typed?.Errors ?? []).map((detail) => String(detail).trim()).filter(Boolean);
  if (details.length > 0) return Array.from(new Set(details)).join(' ');
  return typed?.ErrorMessage ?? '';
};

/** Reemplaza en la lista los consumos devueltos por la API (asignación). */
export const mergeConsumptions = (
  current: ImportedProviderConsumption[],
  updated: ImportedProviderConsumption[]
): ImportedProviderConsumption[] => {
  const byId = new Map(updated.map((item) => [item.id, item]));
  return current.map((item) => byId.get(item.id) ?? item);
};

// ---- origen "consumo de proveedor" en el detalle del consumo de protocolo (spec 005, US8) ----

/**
 * Vínculo a los consumos importados del proveedor. Desde la spec 006 la solapa se integró en "Consumos": el
 * detalle del proveedor redirige `?tab=importedConsumptions` a la grilla unificada filtrada por origen 5.
 */
export const buildImportedConsumptionsRoute = (providerId: number): string => `/providers/${providerId}?tab=importedConsumptions`;

/** El consumo de protocolo viene de un consumo de proveedor importado (tiene el Guid de origen). */
export const hasSourceProviderConsumption = (consumption: { sourceProviderConsumptionId?: string | null } | null | undefined): boolean =>
  !!consumption?.sourceProviderConsumptionId && consumption.sourceProviderConsumptionId.trim() !== '';

/**
 * `Provider.id` (int, el de la ruta) del proveedor cuyo `person.personIdentifier` (Guid, el que traen los consumos)
 * es `identifier`. Undefined si no está en la lista.
 */
export const findProviderRouteId = (
  providers: { id: number; person?: { personIdentifier?: string | null } | null }[] | null | undefined,
  identifier: string | null | undefined
): number | undefined => {
  const key = (identifier ?? '').trim().toLowerCase();
  if (!key) return undefined;
  return (providers ?? []).find((provider) => (provider.person?.personIdentifier ?? '').toLowerCase() === key)?.id;
};

// ---- vista previa del mapeo (spec 007, MP 029) ----

/** Valores editables del mapeo: columna por campo, fecha del documento y moneda por defecto. */
export interface ImportMappingForm {
  columns: Record<ImportMappingField, string | null>;
  /** aaaa-mm-dd o null */
  documentDate: string | null;
  /** Código ISO de 3 letras o null */
  defaultCurrencyCode: string | null;
}

const cleanText = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
};

const cleanDate = (value: unknown): string | null => {
  const text = cleanText(value);
  if (!text) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(text);
  return match ? match[1] : text;
};

const cleanCurrency = (value: unknown): string | null => {
  const text = cleanText(value);
  return text ? text.toUpperCase() : null;
};

/** Formulario del mapeo con los valores del plan de la IA (o de `detectedColumns` si no hay plan). */
export const mappingFormFrom = (
  normalization: ImportNormalization | null | undefined,
  detectedColumns?: ImportMappingColumns | null
): ImportMappingForm => {
  const source: ImportMappingColumns = normalization?.columns ?? detectedColumns ?? {};
  const columns = {} as Record<ImportMappingField, string | null>;
  IMPORT_MAPPING_FIELDS.forEach((field) => {
    columns[field] = cleanText(source[field]);
  });
  return {
    columns,
    documentDate: cleanDate(normalization?.documentDate),
    defaultCurrencyCode: cleanCurrency(normalization?.defaultCurrencyCode),
  };
};

/**
 * Override con SÓLO lo que el usuario cambió respecto del plan original de la IA (`baseline`).
 * Una columna en "(ninguna)" viaja como `null` ("sin columna"). Undefined si no hay cambios.
 * Con `includeColumns = false` (estrategia "document") no se mandan columnas: la IA las ignora.
 */
export const buildMappingOverride = (
  form: ImportMappingForm,
  baseline: ImportMappingForm,
  includeColumns = true
): ImportMappingOverride | undefined => {
  const override: ImportMappingOverride = {};
  if (includeColumns) {
    const columns: ImportMappingColumns = {};
    IMPORT_MAPPING_FIELDS.forEach((field) => {
      const next = cleanText(form.columns[field]);
      if (next !== cleanText(baseline.columns[field])) columns[field] = next;
    });
    if (Object.keys(columns).length > 0) override.columns = columns;
  }
  const date = cleanDate(form.documentDate);
  if (date !== cleanDate(baseline.documentDate)) override.documentDate = date;
  const currency = cleanCurrency(form.defaultCurrencyCode);
  if (currency !== cleanCurrency(baseline.defaultCurrencyCode)) override.defaultCurrencyCode = currency;
  return Object.keys(override).length > 0 ? override : undefined;
};

/** Mismo contenido (para saber si hay cambios sin recalcular). */
export const sameMappingForm = (a: ImportMappingForm, b: ImportMappingForm): boolean => buildMappingOverride(a, b) === undefined;

/** Cuerpo de la vista previa y de la importación: sólo el override (sin override = `{}`, el plan de la IA). */
export const buildImportRequest = (override: ImportMappingOverride | undefined): ImportProviderFileConsumptionsRequest =>
  override ? { mappingOverride: override } : {};

/** Moneda por defecto válida: vacía o 3 letras. */
export const isValidCurrencyCode = (value: string | null | undefined): boolean => {
  const text = cleanText(value);
  return text === null || /^[A-Za-z]{3}$/.test(text);
};

/** Opciones de columna: las del archivo + las del plan que no estén (sin repetir, en el orden del archivo). */
export const mappingColumnOptions = (availableColumns: string[] | null | undefined, current: (string | null | undefined)[] = []): string[] => {
  const options: string[] = [];
  const seen = new Set<string>();
  [...(availableColumns ?? []), ...current].forEach((value) => {
    const text = cleanText(value);
    if (text && !seen.has(text)) {
      seen.add(text);
      options.push(text);
    }
  });
  return options;
};

/** Estrategia "document": la IA extrajo ítems de un documento sin tablas; las columnas no se pueden elegir. */
export const isDocumentStrategy = (normalization: ImportNormalization | null | undefined): boolean =>
  String(normalization?.strategy ?? '').toLowerCase() === 'document';

/** Confianza baja (< 60 %); null / undefined no es baja (estrategia tabular, determinística). */
export const isLowConfidence = (confidence: number | null | undefined): boolean =>
  confidence !== null && confidence !== undefined && !Number.isNaN(Number(confidence)) && Number(confidence) < LOW_CONFIDENCE_THRESHOLD;

/** Confianza 0..1 → porcentaje entero (truncado, como el aviso del backend); undefined si no hay. */
export const confidencePercent = (confidence: number | null | undefined): number | undefined => {
  if (confidence === null || confidence === undefined || Number.isNaN(Number(confidence))) return undefined;
  return Math.trunc(Math.max(0, Math.min(1, Number(confidence))) * 100);
};

/** El error es un 409 de la API (otro mapeo al importar, o importados que no se pueden eliminar). */
export const isConflictError = (error: unknown): boolean => Number((error as Partial<ErrorType> | undefined)?.ErrorCode) === 409;

// ---- sugerencia de paciente desde el archivo (spec 007, US3) ----

const normalizeReference = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();
const digitsOf = (value: string | null | undefined): string => (value ?? '').replace(/\D+/g, '');

/** Referencia de paciente común a todos los consumos (sin espacios de borde ni mayúsculas); undefined si difieren o falta en alguno. */
export const commonPatientReference = (consumptions: Pick<ImportedProviderConsumption, 'patientReference'>[]): string | undefined => {
  if (consumptions.length === 0) return undefined;
  const first = (consumptions[0].patientReference ?? '').trim();
  if (!first) return undefined;
  const key = normalizeReference(first);
  return consumptions.every((item) => normalizeReference(item.patientReference) === key) ? first : undefined;
};

/**
 * Paciente del protocolo cuyo número en protocolo o historia clínica coincide EXACTAMENTE con la referencia del archivo:
 * primero el texto (sin espacios de borde, sin distinguir mayúsculas), después sólo los dígitos. Sólo si la coincidencia
 * es única; nunca por parecido.
 */
export const findSuggestedPatient = <T extends Pick<ProtocolPatient, 'numberInProtocol' | 'medicalRecordNumber'>>(
  patients: T[],
  reference: string | null | undefined
): T | undefined => {
  const key = normalizeReference(reference);
  if (!key) return undefined;
  const unique = (matches: T[]) => (matches.length === 1 ? matches[0] : undefined);
  const exact = patients.filter(
    (patient) => normalizeReference(patient.numberInProtocol) === key || normalizeReference(patient.medicalRecordNumber) === key
  );
  if (exact.length > 0) return unique(exact);
  const digits = digitsOf(reference);
  if (!digits) return undefined;
  return unique(patients.filter((patient) => digitsOf(patient.numberInProtocol) === digits || digitsOf(patient.medicalRecordNumber) === digits));
};
